const CMS_BASE = 'https://cms5.autoshopee.com/api';
const PLANS_PATH = '/pricing-plans?filters[active][$eq]=true&sort=sort:asc&pagination[pageSize]=100&populate=*';
const PROMOTIONS_PATH = '/promotions?sort=endAt:desc&pagination[pageSize]=100&populate=*';
const TIMEOUT_MS = 3000;

const SERVICE_GROUPS = [
  {
    label: '🏚 AutoShopee | ShopManager',
    name: 'autoshopee',
    products: ['shopmanager', 'device_addon'],
  },
  {
    label: '⛱ Extension Kit',
    name: 'extension',
    products: ['kit'],
  },
  {
    label: '💡Subcheo | Follow',
    name: 'subcheo',
    products: ['subcheo_points'],
  },
  {
    label: '🚀 Sao chép Tiktok / Lazada',
    name: 'tiktok-lazada',
    products: ['copy_addon'],
  },
];

let pricingPromise;

const visibleOn = (channels, channel) => !channels || !channels.length || channels.includes(channel);

const roundThousand = (value) => Math.round(value / 1000) * 1000;

const getJson = async (path) => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const response = await fetch(`${CMS_BASE}${path}`, {
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`CMS_HTTP_${response.status}`);
    }

    return (await response.json()).data;
  } finally {
    clearTimeout(timeout);
  }
};

const getPricing = () => {
  if (!pricingPromise) {
    pricingPromise = Promise.all([getJson(PLANS_PATH), getJson(PROMOTIONS_PATH)]).then(
      ([plans, promotions]) => {
        if (!plans?.length) {
          throw new Error('CMS_PRICING_EMPTY');
        }

        return {
          plans,
          promotions: promotions || [],
        };
      },
    );
  }

  return pricingPromise;
};

export const applyPromotion = (plan, promotions, now = new Date(), channel = 'payment') => {
  const time = now.getTime();
  const promotion = promotions
    .filter((item) => visibleOn(item.channels, channel) && item.planCodes?.includes(plan.code))
    .filter((item) => Date.parse(item.startAt) <= time && time <= Date.parse(item.endAt))
    .sort((left, right) => (right.priority ?? 0) - (left.priority ?? 0))[0];

  if (!promotion) {
    return plan;
  }

  const output = {
    ...plan,
    promotion,
    badge: promotion.badge || plan.badge,
  };
  const value = promotion.value ?? 0;

  if (promotion.type === 'percent' && value > 0 && value < 100) {
    output.price = roundThousand((plan.price * (100 - value)) / 100);
    output.priceOrigin = plan.priceOrigin ?? plan.price;
  } else if (promotion.type === 'fixed_price' && value > 0) {
    output.price = value;
    output.priceOrigin = plan.priceOrigin ?? plan.price;
  } else if (promotion.type === 'bonus_months') {
    output.monthsBonus = (plan.monthsBonus || 0) + value;
  }

  if (promotion.giftText) {
    output.gifts = [...(plan.gifts || []), { text: promotion.giftText }];
  }

  return output;
};

export const resolvePlans = (pricing, channel = 'payment', now = new Date()) => {
  return pricing.plans
    .filter((plan) => plan.active !== false && visibleOn(plan.channels, channel))
    .sort((left, right) => (left.sort || 0) - (right.sort || 0))
    .map((plan) => applyPromotion(plan, pricing.promotions, now, channel));
};

export const totalMonths = (plan) => (plan.monthsPaid ?? 0) + (plan.monthsBonus ?? 0);

export const perMonth = (plan) => {
  const months = totalMonths(plan);
  return months ? roundThousand(plan.price / months) : 0;
};

const mapService = (plan) => {
  const months = totalMonths(plan);
  const description = (plan.features || [])
    .map((item) => item.text)
    .filter((text) => text !== plan.subtitle);

  if (plan.monthsBonus > 0) {
    description.unshift(`Tặng ${plan.monthsBonus} tháng`);
  }

  return {
    id: plan.code,
    name: plan.title || plan.plan,
    plan: plan.plan,
    title: plan.title,
    subTitle: plan.subtitle || '',
    price: plan.price,
    beforePrice: plan.priceOrigin || 0,
    totalMonths: months,
    description,
    promotion: (plan.gifts || []).map((item) => item.text),
    hotPrice: plan.showPerMonth && months ? `Chỉ ${Math.round(perMonth(plan) / 1000)}k/tháng` : '',
    bestChoice: Boolean(plan.bestChoice),
    logo: plan.logo,
    badge: plan.badge,
    disabled: Boolean(plan.disabled),
    promotionStartDate: '',
    promotionEndDate: '',
  };
};

export const getCmsListService = async () => {
  const plans = resolvePlans(await getPricing());

  return SERVICE_GROUPS.map((group) => ({
    label: group.label,
    name: group.name,
    value: plans.filter((plan) => group.products.includes(plan.product)).map(mapService),
  }));
};

export const getFollowPackages = async () => {
  const plans = resolvePlans(await getPricing());

  return plans
    .filter((plan) => plan.product === 'subcheo_follow')
    .map((plan) => ({
      id: `Gói ${plan.plan}`,
      title: plan.title,
      subTitle: plan.subtitle || '',
      originalPrice: plan.priceOrigin || 0,
      salePrice: plan.price,
      isHot: Boolean(plan.badge),
      isLargePrice: plan.price >= 400000,
      checkoutUrl: plan.checkoutUrl,
    }));
};

const followOf = (title = '') => {
  const match = title.match(/\d{1,3}(?:\.\d{3})+|\d+/);
  return match ? Number(match[0].replace(/\./g, '')) : null;
};

const discountOf = (plan) => (plan.priceOrigin && plan.priceOrigin > plan.price
  ? Math.round((1 - plan.price / plan.priceOrigin) * 100)
  : 0);

const POPUP_BANNERS = {
  'subcheo-follow-popup': '/img/promo/subcheo-follow.webp',
};

const isHotBadge = (badge) => (badge && badge.toUpperCase().includes('HOT') ? 1 : 0);

export const getActivePopup = async (channel = 'payment', now = new Date()) => {
  const pricing = await getPricing();
  const time = now.getTime();
  const promotion = pricing.promotions
    .filter((item) => item.popup && visibleOn(item.channels, channel))
    .filter((item) => Date.parse(item.startAt) <= time && time <= Date.parse(item.endAt))
    .sort((left, right) => (right.priority ?? 0) - (left.priority ?? 0))[0];

  if (!promotion) {
    return null;
  }

  const allPlans = resolvePlans(pricing, channel, now)
    .filter((plan) => promotion.planCodes?.includes(plan.code))
    .map((plan) => ({
      ...plan,
      follow: followOf(plan.title),
      discount: discountOf(plan),
    }));

  if (!allPlans.length) {
    return null;
  }

  const plans = [...allPlans]
    .sort((left, right) => isHotBadge(right.badge) - isHotBadge(left.badge))
    .slice(0, 3)
    .sort((left, right) => left.price - right.price);
  const featuredDiscount = Math.max(0, ...plans.map((plan) => plan.discount));
  const follows = allPlans.map((plan) => plan.follow).filter((value) => value !== null);

  const url = new URL(allPlans[0].checkoutUrl || 'https://subcheo.vn/#pricing');
  url.searchParams.set('utm_source', 'payment.autoshopee.com');
  url.searchParams.set('utm_medium', 'popup');
  url.searchParams.set('utm_content', promotion.code);

  return {
    code: promotion.code,
    title: promotion.title || '',
    description: promotion.description || '',
    eyebrow: promotion.badge || 'Subcheo × AutoShopee',
    banner: POPUP_BANNERS[promotion.code] || promotion.banner || '',
    ctaLabel: promotion.ctaLabel || 'Xem bảng giá',
    href: url.toString(),
    minPrice: Math.min(...allPlans.map((plan) => plan.price)),
    minFollow: follows.length ? Math.min(...follows) : null,
    maxFollow: follows.length ? Math.max(...follows) : null,
    maxDiscount: Math.max(0, ...allPlans.map((plan) => plan.discount)),
    plans: plans.map((plan) => ({
      code: plan.code,
      title: plan.title,
      follow: plan.follow,
      price: plan.price,
      priceOrigin: plan.priceOrigin || 0,
      discount: plan.discount,
      featured: featuredDiscount > 0 && plan.discount === featuredDiscount,
    })),
  };
};
